import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { plyxApi, plyxMediaUrl } from "@/lib/plyxApi";
import { Loader2, Play } from "lucide-react";

// /plyx/embed/:videoId — minimal chrome-less player designed to live
// inside an <iframe> on external blogs/sites. Every embed is an inbound
// link back to Plyx via the persistent footer bar.
export default function PlyxEmbed() {
  const { videoId } = useParams();
  const [video, setVideo] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    plyxApi.get(`/plyx/videos/${videoId}`).then((r) => setVideo(r.data)).catch(() => setError(true));
  }, [videoId]);

  const watchUrl = `${window.location.origin}/plyx/v/${videoId}`;
  const isPhoto = video?.media_type === "photo";
  const isNative = !isPhoto && (video?.video_url || "").startsWith("/api/files/");
  const thumb = video?.thumbnail_url ? plyxMediaUrl(video.thumbnail_url) : undefined;

  return (
    <div className="fixed inset-0 bg-black text-white flex flex-col" data-testid="plyx-embed">
      <div className="flex-1 relative overflow-hidden">
        {error && (
          <div className="absolute inset-0 flex items-center justify-center text-sm text-white/70" data-testid="plyx-embed-error">
            This video is no longer available.
          </div>
        )}
        {!video && !error && (
          <div className="absolute inset-0 flex items-center justify-center">
            <Loader2 className="w-6 h-6 animate-spin text-white/70" />
          </div>
        )}
        {video && isNative && (
          <video
            src={plyxMediaUrl(video.video_url)}
            poster={thumb}
            controls
            playsInline
            preload="metadata"
            className="absolute inset-0 w-full h-full object-contain"
            data-testid="plyx-embed-video"
          />
        )}
        {video && isPhoto && (
          <img src={plyxMediaUrl(video.video_url)} alt={video.caption || "Plyx photo"} className="absolute inset-0 w-full h-full object-contain" data-testid="plyx-embed-photo" />
        )}
        {video && !isNative && !isPhoto && (
          <a href={watchUrl} target="_blank" rel="noreferrer" className="absolute inset-0 block" data-testid="plyx-embed-external">
            {thumb && <img src={thumb} alt="" className="absolute inset-0 w-full h-full object-cover opacity-70" />}
            <span className="absolute inset-0 flex flex-col items-center justify-center gap-3">
              <span className="w-16 h-16 rounded-full bg-white/15 backdrop-blur-md border border-white/30 flex items-center justify-center">
                <Play className="w-7 h-7 text-white fill-white" />
              </span>
              <span className="text-sm font-semibold">Watch on Plyx</span>
            </span>
          </a>
        )}
      </div>
      <a
        href={watchUrl}
        target="_blank"
        rel="noreferrer"
        className="flex items-center justify-between gap-2 px-4 py-2.5 bg-[#0A0A0A] border-t border-white/10 hover:bg-white/5 transition-colors"
        data-testid="plyx-embed-footer"
      >
        <span className="text-lg font-black tracking-tight bg-gradient-to-r from-[#FF2D7A] via-[#FFB42D] to-[#00E0C7] bg-clip-text text-transparent">plyx</span>
        <span className="text-xs text-white/70 truncate">{video?.creator?.handle ? `@${video.creator.handle}` : ""}</span>
        <span className="text-xs font-semibold whitespace-nowrap">Watch on Plyx ↗</span>
      </a>
    </div>
  );
}
